import { strip } from '@tone-row/strip-comments';
import { encode } from 'html-entities';

const getEdgeBreakIndex = (line) => line.search(/[^\\](: |：)/);
const getFeaturesIndex = (line) => {
  const m = /(^|\s)(#|\.|\[)/.exec(line);
  return m?.index ?? line.length;
};
const featuresRe = /(?<replace>(?<id>#[\w-]+)?(?<classes>(\.[a-zA-Z]{1}[\w-]*)*)?(?<attributes>(\[[^\]=]+(?<attributeValue>=(".*"|'.*'|.*))?\])*))/gs;

function getFeatureData(_line) {
  let line = _line.slice(0).trim();
  let match;
  let id = "";
  let classes = "";
  let attributes = "";
  while ((match = featuresRe.exec(line)) != null) {
    if (!match.groups) continue;
    if (!match.groups.replace) break;
    if (match.groups.id) id = match.groups.id.slice(1);
    if (match.groups.classes) classes = match.groups.classes;
    if (match.groups.attributes) attributes = match.groups.attributes;
    if (match.groups.replace) line = line.replace(match.groups.replace, "").trim();
  }
  const data = {};
  if (attributes) {
    const attrRe = /\[(?<key>[^\]=]+)(?<attributeValue>=(?<rawValue>'(?<value1>[^']+)'|"(?<value2>[^"]+)"|(?<value3>[^\]]+)))?\]/g;
    let attrMatch;
    while ((attrMatch = attrRe.exec(attributes)) != null) {
      if (!attrMatch.groups) continue;
      const key = attrMatch.groups.key;
      if (!key) continue;
      const hasAttributeValue = attrMatch.groups.attributeValue !== void 0;
      if (!hasAttributeValue) {
        data[key] = true;
        continue;
      }
      let value = attrMatch.groups.value1 ?? attrMatch.groups.value2 ?? attrMatch.groups.value3 ?? "";
      const userSuppliedString = attrMatch.groups.rawValue !== value;
      if (!userSuppliedString && !isNaN(Number(value))) {
        value = Number(value);
      }
      data[key] = value;
    }
  }
  return {
    id,
    classes,
    data,
    line
  };
}

function matchAndRemovePointers(line) {
  const pointerRe = /(?<replace>(^|[^\\])[(（](?<pointer>((?<id>#[\w-]+)|(?<class>\.[a-zA-Z]{1}[\w]*)|(?<label>[^)）]+)))[)）])/g;
  let pointerMatch;
  const pointers = [];
  let lineWithPointersRemoved = line.slice(0);
  while ((pointerMatch = pointerRe.exec(line)) != null) {
    if (!pointerMatch.groups) continue;
    if (pointerMatch.groups.pointer) {
      if (pointerMatch.groups.id) {
        pointers.push(["id", pointerMatch.groups.id.slice(1)]);
      } else if (pointerMatch.groups.class) {
        pointers.push(["class", pointerMatch.groups.class.slice(1)]);
      } else if (pointerMatch.groups.label) {
        pointers.push(["label", pointerMatch.groups.label]);
      }
    }
    if (pointerMatch.groups.replace)
      lineWithPointersRemoved = lineWithPointersRemoved.replace(pointerMatch.groups.replace, "").trim();
  }
  return [pointers, lineWithPointersRemoved];
}

class ParseError extends Error {
  startLineNumber;
  endLineNumber;
  startColumn;
  endColumn;
  code;
  constructor(message, startLineNumber, endLineNumber, startColumn, endColumn, code) {
    super(message);
    this.name = "ParseError";
    this.startLineNumber = startLineNumber;
    this.endLineNumber = endLineNumber;
    this.startColumn = startColumn;
    this.endColumn = endColumn;
    this.code = code;
  }
}

function getIndentSize(line) {
  const match = line.match(/^\s*/);
  return match ? match[0].length : 0;
}

function parse(text) {
  const nodes = [];
  const edges = [];
  text = text.replace(/(https?:)\/\//g, "$1\\/\\/");
  let lines = strip(text, { preserveNewlines: true }).split(/\n/g);
  lines = lines.map((line) => line.replace(/\\n/g, "\n"));
  let lineNumber = 0;
  const nodeIds = [];
  const edgeIds = [];
  const allAncestors = [[]];
  const unresolvedEdges = [];
  const containerParentIds = [];
  for (let line of lines) {
    ++lineNumber;
    const originalLine = line;
    if (!line.trim()) continue;
    let isContainerStart = false;
    let isContainerEnd = false;
    if (line[line.length - 1] === "{") {
      line = line.slice(0, -1);
      isContainerStart = true;
    }
    if (/^\s*\}/.test(line)) {
      line = line.replace("}", "");
      isContainerEnd = true;
      containerParentIds.pop();
    }
    const indentSize = getIndentSize(line);
    let ancestors = allAncestors[allAncestors.length - 1] || [];
    if (indentSize === 0 && line[0] === "(") {
      const [pointers2] = matchAndRemovePointers(line);
      allAncestors[allAncestors.length - 1] = [pointers2];
      continue;
    }
    const ancestor = findParent(indentSize, ancestors);
    let edgeLabel = "";
    const edgeBreakIndex = getEdgeBreakIndex(line);
    if (edgeBreakIndex > -1) {
      edgeLabel = line.slice(0, edgeBreakIndex + 1);
      line = line.slice(edgeBreakIndex + 2).trim();
    }
    if (indentSize === 0 && edgeLabel) {
      throw new ParseError(
        `Line ${lineNumber}: Edge label without parent`,
        lineNumber,
        lineNumber,
        0,
        edgeLabel.length + 1,
        "EDGE_LABEL_WITHOUT_PARENT"
      );
    }
    line = line.trim();
    const featuresIndex = getFeaturesIndex(line);
    const { classes, data, ...rest } = getFeatureData(line.slice(featuresIndex));
    let id = rest.id;
    line = line.slice(0, featuresIndex);
    const [pointers, lineWithPointersRemoved] = matchAndRemovePointers(line);
    line = lineWithPointersRemoved;
    if (pointers.length > 1) {
      throw new ParseError(
        `Line ${lineNumber}: Can't create multiple pointers on same line`,
        lineNumber,
        lineNumber,
        0,
        originalLine.length + 1,
        "MULTIPLE_POINTERS_ON_SAME_LINE"
      );
    }
    let label = line;
    label = label.replace(/\\([:：\(\)\（\）{}\[\]])/g, "$1").replace(/\\([#\.\/])/g, "$1");
    let lineDeclaresNode = !!id || !!label || !!classes || Object.keys(data).length > 0;
    if (!lineDeclaresNode && isContainerStart) {
      lineDeclaresNode = true;
      id = `n${lineNumber}`;
    }
    if (pointers.length > 0 && isContainerStart) {
      throw new ParseError(
        `Line ${lineNumber}: Can't create pointer and container on same line`,
        lineNumber,
        lineNumber,
        originalLine.length,
        originalLine.length + 1,
        "POINTER_AND_CONTAINER_ON_SAME_LINE"
      );
    }
    if (lineDeclaresNode && pointers.length > 0) {
      throw new ParseError(
        `Line ${lineNumber}: Can't create node and pointer on same line`,
        lineNumber,
        lineNumber,
        indentSize + 1,
        originalLine.length + 1,
        "NODE_AND_POINTER_ON_SAME_LINE"
      );
    }
    if (lineDeclaresNode && !id) {
      id = `n${lineNumber}`;
    }
    if (lineDeclaresNode && nodeIds.includes(id)) {
      throw new ParseError(
        `Line ${lineNumber}: Duplicate node id "${id}"`,
        lineNumber,
        lineNumber,
        indentSize + 1,
        originalLine.length + 1,
        "DUPLICATE_NODE_ID"
      );
    }
    nodeIds.push(id);
    if (lineDeclaresNode) {
      const _data = {
        label,
        id,
        classes,
        ...data
      };
      if (isContainerStart) {
        _data.isParent = true;
      }
      const node = {
        data: _data,
        parser: {
          lineNumber
        }
      };
      const containerParentId = containerParentIds[containerParentIds.length - 1];
      if (containerParentId) {
        node.data.parent = containerParentId;
      }
      nodes.push(node);
    }
    if (isContainerStart) {
      containerParentIds.push(id);
      allAncestors.push([]);
    }
    if (isContainerEnd) {
      allAncestors.pop();
    }
    if (ancestor) {
      const { line: newLabel, ...edgeData } = getFeatureData(edgeLabel);
      edgeLabel = newLabel;
      if (isId(ancestor)) {
        if (lineDeclaresNode) {
          let edgeId = edgeData.id;
          if (!edgeId) {
            edgeId = `${ancestor}-${id}-1`;
          }
          if (edgeIds.includes(edgeId)) {
            throw new ParseError(
              `Line ${lineNumber}: Duplicate edge id "${edgeId}"`,
              lineNumber,
              lineNumber,
              indentSize + 1,
              // get length of edge id
              indentSize + 1 + edgeId.length + 1,
              "DUPLICATE_EDGE_ID"
            );
          }
          edgeIds.push(edgeId);
          edges.push({
            source: ancestor,
            target: id,
            parser: {
              lineNumber
            },
            data: {
              id: edgeId,
              label: edgeLabel,
              classes: edgeData.classes,
              ...edgeData.data
            }
          });
        }
        for (const [pointerType, pointerId] of pointers) {
          unresolvedEdges.push({
            source: ancestor,
            target: [pointerType, pointerId],
            lineNumber,
            label: edgeLabel,
            id: edgeData.id,
            classes: edgeData.classes,
            otherData: edgeData.data
          });
        }
      } else {
        for (const sourcePointerArray of ancestor) {
          if (lineDeclaresNode) {
            unresolvedEdges.push({
              source: sourcePointerArray,
              target: id,
              lineNumber,
              label: edgeLabel,
              id: edgeData.id,
              classes: edgeData.classes,
              otherData: edgeData.data
            });
          }
          for (const targetPointerArray of pointers) {
            unresolvedEdges.push({
              source: sourcePointerArray,
              target: targetPointerArray,
              lineNumber,
              label: edgeLabel,
              id: edgeData.id,
              classes: edgeData.classes,
              otherData: edgeData.data
            });
          }
        }
      }
    }
    if (!lineDeclaresNode) {
      ancestors[indentSize] = pointers;
    } else {
      ancestors[indentSize] = id;
    }
    ancestors = ancestors.slice(0, indentSize + 1);
  }
  for (const { source, target, lineNumber: lineNumber2, label, otherData, ...rest } of unresolvedEdges) {
    const sourceNodes = isPointerArray(source) ? getNodesFromPointerArray(nodes, edges, source) : nodes.filter((n) => n.data.id === source);
    const targetNodes = isPointerArray(target) ? getNodesFromPointerArray(nodes, edges, target) : nodes.filter((n) => n.data.id === target);
    if (sourceNodes.length === 0 || targetNodes.length === 0) continue;
    for (const sourceNode of sourceNodes) {
      for (const targetNode of targetNodes) {
        const source2 = sourceNode.data.id;
        const target2 = targetNode.data.id;
        const data = {
          ...rest,
          ...otherData,
          label
        };
        if (!data.id) {
          let inc = 1;
          let edgeId = `${source2}-${target2}-${inc}`;
          while (edgeIds.includes(edgeId)) {
            ++inc;
            edgeId = `${source2}-${target2}-${inc}`;
          }
          data.id = edgeId;
        }
        if (edgeIds.includes(data.id)) {
          const line = lines[lineNumber2 - 1];
          if (!line) continue;
          throw new ParseError(
            `Line ${lineNumber2}: Duplicate edge id "${data.id}"`,
            lineNumber2,
            lineNumber2,
            0,
            line.length + 1,
            "DUPLICATE_EDGE_ID"
          );
        }
        edgeIds.push(data.id);
        edges.push({
          source: source2,
          target: target2,
          parser: {
            lineNumber: lineNumber2
          },
          data
        });
      }
    }
  }
  return {
    nodes,
    edges
  };
}
function findParent(indentSize, ancestors) {
  let parent = null;
  let i = indentSize - 1;
  while (!parent && i >= 0) {
    const ancestor = ancestors[i];
    if (ancestor) parent = ancestor;
    i--;
  }
  return parent;
}
function getNodesFromPointerArray(nodes, edges, [pointerType, value]) {
  const entities = [...nodes, ...edges];
  switch (pointerType) {
    case "id":
      return entities.filter((node) => node.data.id === value);
    case "class":
      return entities.filter(
        (node) => typeof node.data.classes === "string" && node.data.classes.split(".").includes(value)
      );
    case "label":
      return entities.filter((node) => node.data.label === value);
  }
}
function isPointerArray(x) {
  return Array.isArray(x) && x.length === 2;
}
function isId(id) {
  return typeof id === "string";
}

function toMermaid({ nodes, edges }) {
  const styleLines = [];
  const lines = ["flowchart"];
  function getSafe(s) {
    return s.replace(/\s+/g, "_");
  }
  const parents = [];
  for (const node of nodes) {
    const { id, label, classes, ...rest } = node.data;
    if (!id) continue;
    const safeId = getSafe(id);
    if (parents.length) {
      if (rest.parent !== parents[parents.length - 1]) {
        parents.pop();
        lines.push(`${whitespace()}end`);
      }
    }
    if (rest.isParent) {
      lines.push(`${whitespace()}subgraph ${safeId} ["${getSafeLabel(label)}"]`);
      parents.push(safeId);
      continue;
    }
    let before = "[";
    let after = "]";
    if (classes.includes("rounded-rectangle") || classes.includes("roundedrectangle")) {
      before = "(";
      after = ")";
    } else if (classes.includes("ellipse")) {
      before = "([";
      after = "])";
    } else if (classes.includes("circle")) {
      before = "((";
      after = "))";
    } else if (classes.includes("diamond")) {
      before = "{";
      after = "}";
    } else if (classes.includes("hexagon")) {
      before = "{{";
      after = "}}";
    } else if (classes.includes("right-rhomboid")) {
      before = "[/";
      after = "/]";
    } else if (classes.includes("rhomboid")) {
      before = "[\\";
      after = "\\]";
    }
    lines.push(`${whitespace()}${safeId}${before}"${getSafeLabel(label) || " "}"${after}`);
  }
  while (parents.length) {
    parents.pop();
    lines.push(`${whitespace()}end`);
  }
  for (const edge of edges) {
    const { source, target } = edge;
    const { label } = edge.data;
    const safeSource = getSafe(source);
    const safeTarget = getSafe(target);
    const safeLabel = getSafeLabel(label);
    lines.push(`	${safeSource} -${safeLabel ? `- "${safeLabel}" -` : ""}-> ${safeTarget}`);
  }
  return lines.concat(styleLines).join("\n");
  function whitespace() {
    return Array(parents.length + 1).fill("	").join("");
  }
}
function getSafeLabel(unsafe) {
  return encode(unsafe);
}

const languageId = "graphselector";
const defaultTheme = "graphselector-theme";
const defaultThemeDark = "graphselector-theme-dark";
const colors = {
  string: { light: "#251d1d", dark: "#fffcff" },
  attribute: { light: "#8252eb", dark: "#9e81ef" },
  variable: { light: "#00c722", dark: "#00c722" },
  comment: { light: "#808080", dark: "#808080" },
  type: { light: "#4750f3", dark: "#7f96ff" },
  "delimiter.curly": { light: "#251d1d", dark: "#fffcff" }
};
function registerHighlighter(monaco) {
  if (monaco.languages.getLanguages().some((l) => l.id === languageId)) {
    return;
  }
  monaco.languages.register({ id: languageId });
  monaco.languages.setLanguageConfiguration(languageId, {
    /* make sure curly braces are automatically closed */
    autoClosingPairs: [
      { open: "{", close: "}" },
      { open: "[", close: "]" },
      { open: "(", close: ")" }
    ],
    /* indent after opening curly brace */
    onEnterRules: [
      {
        beforeText: new RegExp(`^((?!.*?\\/\\*).*\\{[^}"']*(\\/\\*(.*?)\\*\\/)?)[^}"']*$`, "s"),
        afterText: /^.*\}.*$/,
        action: { indentAction: monaco.languages.IndentAction.IndentOutdent }
      }
    ]
  });
  monaco.languages.setMonarchTokensProvider(languageId, {
    defaultToken: "string",
    tokenizer: {
      root: [
        // Attributes with quoted values (including URLs), numbers, or words
        [/\s*\[\w+\s*=\s*(['"].*?['"]|-?\d*\.?\d+|\w+)\]|\s*\[\w+\]/, "attribute"],
        // URLs (must come after attributes but before edge labels)
        [/\s*https?:\/\/[^\s]+/, "string"],
        // Edge label at start of line (after optional indentation)
        [/^\s+[a-zA-Z][\w-]*:/, "type"],
        // Match edge label starting with letter
        // Variable pointers (including leading space)
        [/ \([^)]+\)/, "variable"],
        [/\([^)]+\)/, "variable"],
        // #id and .class combinations (must come before word rule)
        [/(#[\w-]+(\.[a-zA-Z][\w-]*)*|\.[a-zA-Z][\w-]*(\.[\w-]+)*)/, "attribute"],
        // \/\/ single-line comment... (but not URLs)
        [/^\/\/.*|[^:]\/\/.*/, "comment"],
        [/\/\*/, "comment", "@comment"],
        // Escaped characters (must come before other rules)
        [/\\[[\](){}<>:#.\/]/, "string"],
        // Spaces
        [/\s+/, "string"]
      ],
      comment: [
        [/[^\/*]+/, "comment"],
        [/\/\*/, "comment", "@push"],
        // nested comment
        ["\\*/", "comment", "@pop"],
        [/[\/*]/, "comment"]
      ],
      variable: [
        [/[^)]+/, "variable"],
        [/\)/, "variable", "@pop"]
      ]
    }
  });
  monaco.languages.registerCompletionItemProvider(languageId, {
    triggerCharacters: ["("],
    provideCompletionItems: (model, position) => {
      const textUntilPosition = model.getValueInRange({
        startLineNumber: position.lineNumber,
        startColumn: 1,
        endLineNumber: position.lineNumber,
        endColumn: position.column
      });
      if (textUntilPosition.indexOf("(") >= 0) {
        const parsed = parse(model.getValue());
        const suggestions = parsed.nodes.filter((node) => !!node.data.label && node.data.label !== "()").map((node) => ({
          label: node.data.label,
          kind: monaco.languages.CompletionItemKind.Value,
          // insert the label, replacing the closing parenthesis
          insertText: node.data.label + ")",
          range: {
            startLineNumber: position.lineNumber,
            endLineNumber: position.lineNumber,
            startColumn: position.column,
            // replace the closing parenthesis
            endColumn: position.column + 1
          }
        }));
        return {
          suggestions
        };
      }
      return { suggestions: [] };
    }
  });
  monaco.editor.defineTheme(defaultTheme, {
    base: "vs",
    inherit: false,
    // colors: {},
    rules: Object.entries(colors).map(([token, value]) => ({
      token,
      foreground: value.light
    })),
    // Define bracket colors
    colors: {}
  });
  monaco.editor.defineTheme(defaultThemeDark, {
    base: "vs-dark",
    inherit: false,
    // colors: {},
    rules: Object.entries(colors).map(([token, value]) => ({
      token,
      foreground: value.dark
    })),
    // Define bracket colors
    colors: {}
  });
}

var highlight = /*#__PURE__*/Object.freeze({
  __proto__: null,
  colors: colors,
  defaultTheme: defaultTheme,
  defaultThemeDark: defaultThemeDark,
  languageId: languageId,
  registerHighlighter: registerHighlighter
});

function toCytoscapeElements(parsed) {
  const elements = [];
  if (!parsed) return elements;
  for (const { data, parser = {} } of parsed.nodes) {
    const { classes: _classes = "", ...rest } = data;
    const classes = _classes.split(".").join(" ");
    elements.push({
      classes,
      data: {
        ...parser,
        ...rest
      }
    });
  }
  for (const { source, target, data, parser = {} } of parsed.edges) {
    const { classes: _classes = "", ...rest } = data;
    const classes = _classes.split(".").join(" ");
    elements.push({
      classes,
      data: {
        ...parser,
        source,
        target,
        ...rest
      }
    });
  }
  return elements;
}

function dataToString(data) {
  let dataString = "";
  for (const key in data) {
    const value = data[key];
    dataString += `[${key}`;
    if (value === true) {
      dataString += "]";
      continue;
    } else if (typeof value === "string") {
      if (value.includes('"')) {
        dataString += `='${value}']`;
        continue;
      }
      dataString += `="${value}"]`;
      continue;
    } else if (typeof value === "number") {
      dataString += `=${value}]`;
      continue;
    }
  }
  return dataString;
}

function addClassesToEdge({ line, classNames }) {
  const indent = line.match(/^\s*/)?.[0] || "";
  line = line.replace(/^\s*/, "");
  let containerStart = "";
  if (line.endsWith(" {")) {
    containerStart = " {";
    line = line.slice(0, -2);
  }
  let edge = "";
  const edgeBreakIndex = getEdgeBreakIndex(line);
  if (edgeBreakIndex !== -1) {
    edge = line.slice(0, edgeBreakIndex + 1);
    line = line.slice(edgeBreakIndex + 1);
  }
  const noEdge = !edge;
  const startOfFeatures = getFeaturesIndex(edge);
  let features = "";
  if (startOfFeatures === 0) {
    features = featuresRe.exec(edge)?.[0] || "";
    edge = edge.slice(features.length);
    featuresRe.lastIndex = 0;
  }
  const divider = noEdge ? ": " : "";
  if (!features) {
    const completeEdge = [`.${classNames.join(".")}`, edge.trim()].filter(Boolean).join(" ").trim();
    return indent + completeEdge + divider + line + containerStart;
  }
  const { classes, data, id = "" } = getFeatureData(features);
  let newFeatureString = " ";
  if (id) newFeatureString += `#${id}`;
  if (classes) newFeatureString += classes;
  let classNameString = "";
  for (const className of classNames)
    if (!classes.includes(className)) classNameString += `.${className}`;
  newFeatureString += classNameString;
  if (Object.keys(data).length) newFeatureString += dataToString(data);
  const edgeWithFeatures = [newFeatureString.trim(), edge.trim()].filter(Boolean).join(" ").trim();
  return indent + edgeWithFeatures + line + containerStart;
}

function addClassesToNode({ line, classNames }) {
  const indent = line.match(/^\s*/)?.[0] || "";
  line = line.replace(/^\s*/, "");
  let containerStart = "";
  if (line.endsWith(" {")) {
    containerStart = " {";
    line = line.slice(0, -2);
  }
  let edge = "";
  const edgeBreakIndex = getEdgeBreakIndex(line);
  if (edgeBreakIndex !== -1) {
    edge = line.slice(0, edgeBreakIndex + 1);
    line = line.slice(edgeBreakIndex + 1);
  }
  const featuresIndex = getFeaturesIndex(line);
  let features = "";
  if (featuresIndex !== -1) {
    features = line.slice(featuresIndex);
    line = line.slice(0, featuresIndex);
  }
  if (!features) {
    return indent + edge + line + ` .${classNames.join(".")}` + containerStart;
  }
  const { classes, data, id = "" } = getFeatureData(features);
  let newFeatureString = " ";
  if (id) newFeatureString += `#${id}`;
  if (classes) newFeatureString += classes;
  let classNameString = "";
  for (const className of classNames)
    if (!classes.includes(className)) classNameString += `.${className}`;
  newFeatureString += classNameString;
  if (Object.keys(data).length) newFeatureString += dataToString(data);
  return indent + edge + line + newFeatureString + containerStart;
}

function addDataAttributeToNode({
  line,
  name,
  value
}) {
  const indent = line.match(/^\s*/)?.[0] || "";
  line = line.replace(/^\s*/, "");
  let containerStart = "";
  if (line.endsWith(" {")) {
    containerStart = " {";
    line = line.slice(0, -2);
  }
  let edge = "";
  const edgeBreakIndex = getEdgeBreakIndex(line);
  if (edgeBreakIndex !== -1) {
    edge = line.slice(0, edgeBreakIndex + 1);
    line = line.slice(edgeBreakIndex + 1);
  }
  const featuresIndex = getFeaturesIndex(line);
  let features = "";
  if (featuresIndex !== -1) {
    features = line.slice(featuresIndex);
    line = line.slice(0, featuresIndex);
  }
  const dataStr = dataToString({ [name]: value });
  if (!features) {
    return indent + edge + line + ` ${dataStr}` + containerStart;
  }
  const { classes, data, id = "" } = getFeatureData(features);
  let newFeatureString = " ";
  if (id) newFeatureString += `#${id}`;
  if (classes) newFeatureString += classes;
  newFeatureString += dataToString({ ...data, [name]: value });
  return indent + edge + line + newFeatureString + containerStart;
}

function removeClassesFromEdge({
  line,
  classNames
}) {
  const indent = line.match(/^\s*/)?.[0];
  line = line.replace(/^\s*/, "");
  let containerStart = "";
  if (line.endsWith(" {")) {
    containerStart = " {";
    line = line.slice(0, -2);
  }
  let edge = "";
  const edgeBreakIndex = getEdgeBreakIndex(line);
  if (edgeBreakIndex !== -1) {
    edge = line.slice(0, edgeBreakIndex + 1);
    line = line.slice(edgeBreakIndex + 1);
  }
  if (edge.trim()) {
    const labelStart = /\w/.exec(edge)?.index ?? -1;
    const featuresStart = getFeaturesIndex(edge);
    if (labelStart < featuresStart) {
      return indent + edge + line + containerStart;
    }
  }
  for (const className of classNames) {
    const match = new RegExp(`\\.${className}(?<stopCharacter>\\.|$| |:|\uFF1A|\\[)`).exec(edge);
    if (!match) continue;
    const stopCharacter = match.groups?.stopCharacter || "";
    const index = match.index;
    edge = edge.slice(0, index) + stopCharacter + edge.slice(index + match[0].length);
  }
  edge = edge.replace(/^\s*/, "");
  if (edge === "" && line.startsWith(":")) {
    line = line.replace(/^[:：]\s*/, "");
  }
  return indent + edge + line + containerStart;
}

function removeClassesFromNode({
  line,
  classNames
}) {
  const indent = line.match(/^\s*/)?.[0];
  line = line.replace(/^\s*/, "");
  let containerStart = "";
  if (line.endsWith(" {")) {
    containerStart = " {";
    line = line.slice(0, -2);
  }
  let edge = "";
  const edgeBreakIndex = getEdgeBreakIndex(line);
  if (edgeBreakIndex !== -1) {
    edge = line.slice(0, edgeBreakIndex + 1);
    line = line.slice(edgeBreakIndex + 1);
  }
  for (const className of classNames) {
    const match = new RegExp(`\\.${className}(?<stopCharacter>\\.|$| |:|\uFF1A|\\[)`).exec(line);
    if (!match) continue;
    const stopCharacter = match.groups?.stopCharacter || "";
    const index = match.index;
    line = line.slice(0, index) + stopCharacter + line.slice(index + match[0].length);
  }
  line = line.replace(/\s*$/, "");
  return indent + edge + line + containerStart;
}

function removeDataAttributeFromNode({ line, name }) {
  const indent = line.match(/^\s*/)?.[0] || "";
  line = line.replace(/^\s*/, "");
  let containerStart = "";
  if (line.endsWith(" {")) {
    containerStart = " {";
    line = line.slice(0, -2);
  }
  let edge = "";
  const edgeBreakIndex = getEdgeBreakIndex(line);
  if (edgeBreakIndex !== -1) {
    edge = line.slice(0, edgeBreakIndex + 1);
    line = line.slice(edgeBreakIndex + 1);
  }
  const featuresIndex = getFeaturesIndex(line);
  let features = "";
  if (featuresIndex !== -1) {
    features = line.slice(featuresIndex);
    line = line.slice(0, featuresIndex);
  }
  if (!features) {
    return indent + edge + line + containerStart;
  }
  const { classes, data, id = "" } = getFeatureData(features);
  let newFeatureString = "";
  if (id) newFeatureString += `#${id}`;
  if (classes) newFeatureString += classes;
  delete data[name];
  newFeatureString += dataToString(data);
  return indent + (edge + [line, newFeatureString].filter(Boolean).join(" ") + containerStart).trim();
}

const operations = {
  removeClassesFromNode,
  addClassesToNode,
  addDataAttributeToNode,
  removeDataAttributeFromNode,
  addClassesToEdge,
  removeClassesFromEdge
};
function operate(graphText, instruction) {
  const lines = graphText.split("\n");
  const { operation } = instruction;
  if (instruction.lineNumber < 1) throw new Error("lineNumber must be 1-indexed");
  if (instruction.lineNumber > lines.length)
    throw new Error("lineNumber must be less than the number of lines");
  const lineNumber = instruction.lineNumber - 1;
  const [operationKey, operationParams] = operation;
  const operationFunction = operations[operationKey];
  const line = lines[lineNumber];
  const newLine = operationFunction({ line, ...operationParams });
  lines[lineNumber] = newLine;
  return lines.join("\n");
}

const defaultOptions = {
  compact: false
};
function stringify(graph, options = defaultOptions) {
  const lines = [];
  for (const { data: node } of graph.nodes) {
    const labelStr = escapeLabel(node.label);
    let idStr = "";
    if (node.id !== node.label) {
      idStr = `#${node.id}`;
    }
    let classesStr = "";
    if (node.classes)
      classesStr = node.classes.split(" ").map((c) => `.${c}`).join("");
    const data = stringifyData(node);
    const features = [idStr, classesStr, data].filter(Boolean).join("");
    const line = [labelStr, features].filter(Boolean).join(" ");
    let parentIndent = 0;
    let insertEdgeAt = void 0;
    if (options.compact) {
      const targetStr = idStr ? `(${idStr})` : `(${labelStr})`;
      const existingLineIndex = lines.findIndex((line2) => line2.includes(targetStr));
      if (existingLineIndex === -1) {
        lines.push(line);
      } else {
        lines[existingLineIndex] = lines[existingLineIndex].replace(targetStr, line);
        insertEdgeAt = existingLineIndex + 1;
        parentIndent = getIndentSize(lines[existingLineIndex]);
      }
    } else {
      lines.push(line);
    }
    const edges = graph.edges.filter((edge) => edge.source === node.id);
    for (const { target, data: edge } of edges) {
      const targetNode = graph.nodes.find((node2) => node2.data.id === target);
      if (!targetNode) continue;
      let label = escapeLabel(edge.label);
      let id = edge.id;
      if (id && id !== edge.label) {
        id = `#${edge.id}`;
      }
      let classes = "";
      if (edge.classes) {
        classes = edge.classes.split(" ").map((c) => `.${c}`).join("");
      }
      const data2 = stringifyData(edge);
      const features2 = [id, classes, data2].filter(Boolean).join("");
      label = [features2, label].filter(Boolean).join(" ");
      if (label) label = `${label}: `;
      const link = targetNode.data.id !== targetNode.data.label ? `#${targetNode.data.id}` : targetNode.data.label;
      const wrappedLink = `(${link})`;
      const parentIndentStr = " ".repeat(parentIndent);
      const line2 = [parentIndentStr, "  ", label, wrappedLink].filter(Boolean).join("");
      if (options.compact && insertEdgeAt !== void 0) {
        lines.splice(insertEdgeAt, 0, line2);
        insertEdgeAt++;
      } else {
        lines.push(line2);
      }
    }
  }
  return lines.join("\n");
}
function stringifyData(data) {
  return Object.entries(data).map(([key, value]) => {
    if (["id", "label", "classes"].includes(key)) return "";
    if (typeof value === "boolean") return `[${key}]`;
    if (typeof value === "number") return `[${key}=${value}]`;
    if (typeof value === "string") return `[${key}="${value.replace(/"/g, '\\"')}"]`;
    throw new Error(`Invalid data type for property "${key}": ${typeof value}`);
  }).filter(Boolean).join("");
}
function escapeLabel(label) {
  return label.replace(/[:\[\]\(\)]/g, (c) => `\\${c}`).replace(/\n/g, "\\n");
}

export { ParseError, highlight, operate, operations, parse, stringify, toCytoscapeElements, toMermaid };
